import { useState, useEffect, type FormEvent, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import { isMasterSandboxUser } from '../lib/testOverride';
import { cancelVoicePreview } from '../lib/voiceProfile';
import {
  GENDER_OPTIONS,
  formatVoiceProfileLabel,
  resolveVoiceFromMatrix,
  previewVoiceSample,
} from '../lib/voiceProfile';
import { SCREENS } from '../lib/screenRegistry';
import { ROUTES } from '../lib/classroomRoutes';
import { isMaintenancePreviewEmbed } from '../lib/masterAdminAccess';
import './ProSetup.css';

const LOGO = encodeURI('/Assets/logo/mentology- logo.png');

const PRO_SETUP_LANGUAGE_OPTIONS = ['English (Aust)', 'English (Global)'] as const;

const PRO_TEST_DEFAULTS = {
  name: 'John Citizen',
  preferred_name: '',
  address: '123 Ocean Drive, Noosa Heads QLD 4567',
  postcode: '4567',
  country: 'Australia',
  language_target: 'English (Aust)',
  ai_mentor_gender: '',
};

type SetupForm = typeof PRO_TEST_DEFAULTS;

const EMPTY_DEFAULTS: SetupForm = {
  name: '',
  preferred_name: '',
  address: '',
  postcode: '',
  country: '',
  language_target: 'English (Aust)',
  ai_mentor_gender: '',
};

export default function ProSetup() {
  const navigate = useNavigate();
  const { user, masterSandboxUnlocked } = useAuth();
  const [form, setForm] = useState<SetupForm>(EMPTY_DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    if (isMaintenancePreviewEmbed()) return;
    if (!user) {
      navigate(ROUTES.CLASSROOM_PORTAL, { replace: true });
      return;
    }
    if (user.role === 'ORG') {
      navigate(ROUTES.SYSTEM_ADMIN, { replace: true });
      return;
    }
    if (user.accountState === 'INITIALIZING') {
      navigate(ROUTES.EXECUTIVE_INTRO, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (isMaintenancePreviewEmbed()) {
      return () => {
        cancelVoicePreview();
      };
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      cancelVoicePreview();
    };
  }, []);

  useEffect(() => {
    authApi.getBio()
      .then((data) => {
        if (data.bio) {
          const { weather: _weather, ...bioFields } = data.bio as SetupForm & { weather?: string };
          setForm({
            ...EMPTY_DEFAULTS,
            name: bioFields.name ?? '',
            preferred_name: bioFields.preferred_name ?? '',
            address: bioFields.address ?? '',
            postcode: bioFields.postcode ?? '',
            country: bioFields.country ?? '',
            ai_mentor_gender: bioFields.ai_mentor_gender ?? '',
            language_target: bioFields.language_target === 'English (Global)'
              ? 'English (Global)'
              : 'English (Aust)',
          });
        } else if (isMasterSandboxUser(user) || masterSandboxUnlocked || isMaintenancePreviewEmbed()) {
          setForm({ ...PRO_TEST_DEFAULTS });
        }
      })
      .catch(() => {
        if (isMasterSandboxUser(user) || masterSandboxUnlocked || isMaintenancePreviewEmbed()) {
          setForm({ ...PRO_TEST_DEFAULTS });
        }
      });
  }, [user, masterSandboxUnlocked]);

  const voiceEntry = resolveVoiceFromMatrix(form.language_target, form.ai_mentor_gender);

  const update = (field: keyof SetupForm, value: string) => {
    setPreviewError('');
    setForm((current) => ({ ...current, [field]: value }));
  };

  const preventFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const blockEnterSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Enter') return;
    const target = event.target as HTMLElement;
    if (target.tagName === 'BUTTON') return;
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePreview = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!voiceEntry || previewLoading) return;

    setPreviewLoading(true);
    setPreviewError('');

    try {
      await previewVoiceSample(voiceEntry.voiceId, voiceEntry.name, {
        language: voiceEntry.language,
        gender: voiceEntry.gender,
        modelId: voiceEntry.modelId,
      });
    } catch (error) {
      console.error('[Voice preview]', error);
      const apiError = error as { error?: string; code?: string; status?: number };
      setPreviewError(apiError.error || 'Voice preview unavailable. Check ElevenLabs API key on the server.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (loading) return;

    setLoading(true);
    cancelVoicePreview();

    const entry = resolveVoiceFromMatrix(form.language_target, form.ai_mentor_gender);

    const payload = {
      ...form,
      language_target: form.language_target || null,
      ai_mentor_gender: form.ai_mentor_gender || null,
      ai_mentor_name: entry?.name ?? null,
      ai_mentor_voice_id: entry?.voiceId ?? null,
    };

    try {
      await authApi.saveBio(payload);
      sessionStorage.removeItem('mentology.pendingBio');
    } catch (error) {
      sessionStorage.setItem('mentology.pendingBio', JSON.stringify(payload));
      console.warn('[ProSetup] Bio save issue — continuing onboarding', error);
    }

    setLoading(false);

    if (isMaintenancePreviewEmbed()) {
      return;
    }

    navigate(ROUTES.LEARNING_ROOM, { replace: true });
  };

  return (
    <div className="pro-setup-screen" data-screen-id={SCREENS.MPL09}>
      <div className="pro-setup-inner">
        <div className="pro-setup-logo-wrap">
          <img
            src={LOGO}
            alt="Mentology"
            className="pro-setup-logo brand-logo"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>

        <form
          className="pro-setup-form"
          onSubmit={preventFormSubmit}
          onKeyDown={blockEnterSubmit}
          noValidate
        >
          <div className="pro-setup-grid-wrap">
            <div className="pro-setup-grid">
          <label className="pro-setup-label scannable-bullet-text">
            Name: First and Surname
            <input
              className="input-field pro-setup-input"
              type="text"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
            />
          </label>

          <label className="pro-setup-label scannable-bullet-text">
            Preferred Name: (what would you like me to call you)
            <input
              className="input-field pro-setup-input"
              type="text"
              value={form.preferred_name}
              onChange={(e) => update('preferred_name', e.target.value)}
            />
          </label>

          <label className="pro-setup-label scannable-bullet-text">
            Address:
            <input
              className="input-field pro-setup-input"
              type="text"
              value={form.address}
              onChange={(e) => update('address', e.target.value)}
            />
          </label>

          <label className="pro-setup-label scannable-bullet-text">
            Postcode:
            <input
              className="input-field pro-setup-input"
              type="text"
              value={form.postcode}
              onChange={(e) => update('postcode', e.target.value)}
            />
          </label>

          <label className="pro-setup-label scannable-bullet-text">
            Country:
            <input
              className="input-field pro-setup-input"
              type="text"
              value={form.country}
              onChange={(e) => update('country', e.target.value)}
            />
          </label>

          <div className="pro-setup-row-full">
            <div className="pro-setup-voice-row">
              <label className="pro-setup-label scannable-bullet-text">
                Preferred Language
                <select
                  className="input-field pro-setup-input pro-setup-select"
                  value={form.language_target}
                  onChange={(e) => update('language_target', e.target.value)}
                >
                  {PRO_SETUP_LANGUAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <div className="pro-setup-voice-cell">
                <label className="pro-setup-label scannable-bullet-text">
                  AI-Study Partner Voice
                  <select
                    className="input-field pro-setup-input pro-setup-select"
                    value={form.ai_mentor_gender}
                    onChange={(e) => update('ai_mentor_gender', e.target.value)}
                  >
                    <option value="">Select gender</option>
                    {GENDER_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                {voiceEntry && (
                  <p className="pro-setup-voice-profile-line">
                    <span className="pro-setup-mentor-name">
                      Voice Profile: {formatVoiceProfileLabel(voiceEntry)}
                    </span>
                    {' '}
                    <button
                      type="button"
                      className="pro-setup-voice-preview-link"
                      onClick={handlePreview}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      disabled={previewLoading}
                      aria-label="Play voice preview sample"
                    >
                      {previewLoading ? '[ ► loading... ]' : '[ ► tap to hear ]'}
                    </button>
                    {previewError && (
                      <span className="pro-setup-voice-preview-error">{previewError}</span>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

          <div className="pro-setup-action">
            <button
              type="button"
              className="btn-primary pro-setup-save-btn"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'SAVE & ENTER LEARNING SPACE'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
